/**
 * Money is always an integer count of minor units, serialised as a string.
 * Scale is 2 for every currency; display precision is per currency.
 * TZS 1,000 -> "100000" -> "TZS 1,000"
 */

export type Minor = string;
export type CurrencyCode = "TZS" | "USD" | "KES" | "UGX";

export const MONEY_SCALE = 2;

const DISPLAY_DECIMALS: Record<string, number> = {
  TZS: 0,
  KES: 0,
  UGX: 0,
  USD: 2,
};

export function decimalsFor(currency: string): number {
  return DISPLAY_DECIMALS[currency.toUpperCase()] ?? 2;
}

/** Convert a major-unit number (e.g. 1000.5) to minor units without float drift. */
export function toMinor(major: number | string, currency = "TZS"): Minor {
  const decimals = decimalsFor(currency);
  const raw = typeof major === "string" ? major.replace(/[^\d.-]/g, "") : String(major);
  const value = Number(raw);
  if (!Number.isFinite(value)) throw new Error(`Invalid amount: ${major}`);
  return String(Math.round(value * 10 ** decimals) * 10 ** (MONEY_SCALE - decimals));
}

/** Convert minor units to a major-unit number. For display only. */
export function toMajor(minor: Minor | number | bigint, _currency = "TZS"): number {
  const n = typeof minor === "bigint" ? Number(minor) : Number(minor);
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** MONEY_SCALE;
}

/** Minor units -> major units. Storage is always scale 2. */
export function majorValue(minor: Minor | number, _currency = "TZS"): number {
  const n = Number(minor);
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** MONEY_SCALE;
}

export function formatMoney(minor: Minor | number | bigint, currency = "TZS", locale = "en-TZ"): string {
  const code = currency.toUpperCase();
  const decimals = decimalsFor(code);
  const value = majorValue(typeof minor === "bigint" ? Number(minor) : minor, code);
  if (code === "TZS") {
    // Tanzania's shilling is shown as TZS in product copy; Intl uses "TSh".
    return `TZS ${new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value)}`;
  }
  const formatted = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
  return formatted.replace(/\u00A0/g, " ").replace(/\u202F/g, " ");
}

/** Amount only, no currency symbol. */
export function formatAmount(minor: Minor | number, currency = "TZS", locale = "en-TZ"): string {
  const decimals = decimalsFor(currency);
  const value = majorValue(minor, currency);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Compact display for KPI cards: TZS 48.2M */
export function formatCompactMoney(minor: Minor | number, currency = "TZS"): string {
  const n = majorValue(minor, currency);
  const abs = Math.abs(n);
  const symbol = currency;
  if (abs >= 1_000_000_000) return `${symbol} ${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${symbol} ${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${symbol} ${(n / 1_000).toFixed(1)}K`;
  return formatMoney(minor, currency);
}

/** Parse user input ("1,000" / "1000.50") into minor units, or null if invalid. */
export function parseMoneyInput(input: string, currency = "TZS"): Minor | null {
  const cleaned = input.replace(/[^\d.]/g, "");
  if (!cleaned || cleaned === ".") return null;
  if ((cleaned.match(/\./g) ?? []).length > 1) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return toMinor(value, currency);
}

export function addMoney(a: Minor, b: Minor): Minor {
  return String(BigInt(a) + BigInt(b));
}

export function subMoney(a: Minor, b: Minor): Minor {
  return String(BigInt(a) - BigInt(b));
}

export function isPositiveMoney(value: Minor | string): boolean {
  try {
    return BigInt(value) > 0n;
  } catch {
    return false;
  }
}

export function maskMsisdn(msisdn: string): string {
  const digits = msisdn.replace(/\D/g, "");
  if (digits.length < 9) return digits;
  const country = digits.startsWith("255") ? digits.slice(0, 3) : "";
  const rest = country ? digits.slice(3) : digits;
  const head = rest.slice(0, 3);
  const tail = rest.slice(-3);
  return `${country}${head}***${tail}`;
}

/** 0712345678 / +255712345678 / 255712345678 -> 255712345678 */
export function normaliseMsisdn(input: string): string | null {
  let d = input.replace(/\D/g, "");
  if (d.startsWith("00255")) d = d.slice(2);
  if (d.startsWith("0") && d.length === 10) d = `255${d.slice(1)}`;
  if (d.length === 9 && d.startsWith("7") === false && d.startsWith("6") === false) return null;
  if (d.length === 9) d = `255${d}`;
  if (!/^255[6-8]\d{8}$/.test(d)) return null;
  return d;
}
